import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import {
  ButtonComponent,
  IconComponent,
  MenuComponent,
  MenuTriggerStateDirective,
  SkeletonComponent,
  slugify,
  TooltipHoverDirective,
  WebComponentsConfigService,
} from '@evc/web-components';

import { AuthService } from '../../../../clients/core/auth/service/auth.service';
import { permissions } from '../../../../clients/core/types/permission.type';
import { UserService } from '../../../../clients/core/user/user.service';
import { ComputedWebApp } from '../../../../clients/core/user/user.type';
import { PlatformConfigService } from '../../../config/config.service';
import { I18nPlatformService } from '../../../i18n/i18n.service';
import { PlatformTranslatePipe } from '../../../i18n/platform-translate.pipe';
import { ApplicationType } from '../../../types/user-menu.type';
import { NavigationsService } from '../../navigations.service';
import { NAVIGATIONS_MENUS } from '../../types';

@Component({
  selector: 'evc-apps-menu',
  imports: [
    IconComponent,
    ButtonComponent,
    SkeletonComponent,
    PlatformTranslatePipe,
    TooltipHoverDirective,
    MenuComponent,
    MenuTriggerStateDirective,
  ],
  templateUrl: './apps-menu.html',
  styleUrls: ['./apps-menu.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-pending]': 'isPending()',
  },
})
export class AppsMenuComponent {
  menuComponent = viewChild(MenuComponent);
  readonly internalNavButtonClicked = output();

  readonly #authService = inject(AuthService);
  readonly #configService = inject(PlatformConfigService);
  readonly #platformTranslateService = inject(I18nPlatformService);
  readonly #userService = inject(UserService);
  readonly #router = inject(Router);
  readonly #navigationsService = inject(NavigationsService);

  readonly #webComponentsConfigService = inject(WebComponentsConfigService);
  basePath = this.#webComponentsConfigService.getComputed('ASSETS_BASE_PATH');

  readonly isIms = input(false);
  protected readonly isPending = this.#authService.shouldPromptOrganizationSelection;
  protected readonly isApplicationsLoading = this.#userService.applicationsLoading;

  #maybeForceLoginUriSuffix = computed(() => (this.#authService.isAuthenticated() ? '/login' : ''));

  showAdminApp = computed(() => {
    const isAllowed = this.#userService.checkHasPermission(permissions.ORGANIZATION_READ);

    return isAllowed;
  });

  applications = computed<(ComputedWebApp | ApplicationType)[]>(() => {
    if (!this.#configService.useConnect()) {
      return this.#configService.applications;
    }

    const applications = [...(this.#userService.applications() ?? [])];

    if (this.showAdminApp()) {
      const { admin: adminAppUri } = this.#configService.get('uris');

      (applications as unknown as ApplicationType[]).push({
        code: 'organization',
        launchUrl: adminAppUri,
        name: this.#platformTranslateService.tSignal('menus.applications.links.admin')(),
      });
    }

    return applications;
  });

  /** fetch apps when the menu is opened rather than on component load, since floating license capacity is transient */
  #watchMenuOpenToFetchApplications = effect(() => {
    const isOpen = this.menuComponent()?.isOpenInternal();
    if (!isOpen || !this.#configService.useConnect()) return;

    untracked(() => {
      this.#userService.getUserApplications(true);
    });
  });

  isComputedWebApp(app: ApplicationType | ComputedWebApp): app is ComputedWebApp {
    return (app as ComputedWebApp).id !== undefined;
  }

  getAppImgPath(app: ApplicationType): string {
    const appCode: string = app.code.toLowerCase();

    return `${this.basePath()}applications/${appCode}.svg#${appCode}`;
  }

  getAppAriaLabel(app: ComputedWebApp | ApplicationType): string {
    if (!this.isComputedWebApp(app) || app.isAvailable) return app.name;

    return `${app.name}, ${this.#platformTranslateService.t('menus.applications.unavailable')}`;
  }

  handleAppClick(app: ComputedWebApp | ApplicationType): void {
    if (this.isComputedWebApp(app) && !app.isAvailable) return;

    const url = (app as ComputedWebApp).accessUrl || (app as ApplicationType).launchUrl;
    this.#webComponentsConfigService.openLink(url);
  }

  handleHomeClick(): void {
    if (this.#configService.get('APP_NAME') === 'homepage') {
      this.#router.navigateByUrl('/');
      this.internalNavButtonClicked.emit();
    } else {
      const { homepage } = this.#configService.get('uris');
      if (!homepage) return;

      this.#webComponentsConfigService.openLink(`${homepage}${this.#maybeForceLoginUriSuffix()}`);
    }
  }

  handleMarketplaceClick(): void {
    if (this.#configService.get('APP_NAME') === 'homepage') {
      this.#router.navigateByUrl('/marketplace');
      this.internalNavButtonClicked.emit();
    } else {
      const { marketplace } = this.#configService.get('uris');
      if (!marketplace) return;

      this.#webComponentsConfigService.openLink(
        `${marketplace}${this.#maybeForceLoginUriSuffix()}`,
      );
    }
  }

  #watchServiceToAutoOpenMenu = effect(() => {
    const activeMenu = this.#navigationsService.activeMenu();
    const menuComponent = this.menuComponent();
    if (!menuComponent || activeMenu !== NAVIGATIONS_MENUS.APPS) return;

    untracked(() => {
      setTimeout(() => {
        menuComponent.toggle(true);
      });
    });
  });

  readonly slugify = slugify;
}
